/**
 * Phase 46 (Plan 46-07) — Admin global notification routing page.
 *
 * Per UI-SPEC §Surface 2: TopBar + intro paragraph + NotificationRoutingMatrix
 * scoped to `global`. Wrapped in `<AdminRoute>` at the router level
 * (registered by the orchestrator post-merge per parallel_warning).
 *
 * Mutations (create / update / delete) flow through `useGlobalNotificationRoutes`
 * hooks which invalidate `['globalNotificationRoutes']` on success.
 */
import { useState } from 'react';
import { TopBar } from '../../components/ui/TopBar';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/common/Spinner';
import { useToast } from '../../components/ui/Toast';
import { NotificationRoutingMatrix } from '../../components/notifications/NotificationRoutingMatrix';
import type { MatrixRoute } from '../../components/notifications/NotificationRoutingMatrix';
import {
  NotificationRouteEditorModal,
  type EditorMode,
  type EditorPayload,
} from '../../components/notifications/NotificationRouteEditorModal';
import { RemoveRouteConfirmModal } from '../../components/notifications/RemoveRouteConfirmModal';
import {
  useGlobalNotificationRoutes,
  useCreateGlobalNotificationRoute,
  useUpdateGlobalNotificationRoute,
  useDeleteGlobalNotificationRoute,
} from '../../hooks/useGlobalNotificationRoutes';
import type { GlobalNotificationRouteDTO } from '../../services/api/notificationRoutes';
import type { EventKind, Channel } from '../../constants/notificationEnums';
import { ApiClientError } from '../../services/api/client';

interface EditorState {
  open: boolean;
  mode: EditorMode;
  eventKind: EventKind | null;
  channel: Channel | null;
  routeId: number | null;
  initialTarget: string | null;
}

interface RemoveState {
  open: boolean;
  route: GlobalNotificationRouteDTO | null;
}

const EDITOR_CLOSED: EditorState = {
  open: false,
  mode: 'create',
  eventKind: null,
  channel: null,
  routeId: null,
  initialTarget: null,
};

const REMOVE_CLOSED: RemoveState = { open: false, route: null };

function toMatrixRoute(r: GlobalNotificationRouteDTO): MatrixRoute {
  return {
    id: r.id,
    eventKind: r.eventKind,
    channel: r.channel,
    target: r.target,
  };
}

export function GlobalNotificationRoutesPage() {
  const routesQuery = useGlobalNotificationRoutes();
  const createMutation = useCreateGlobalNotificationRoute();
  const updateMutation = useUpdateGlobalNotificationRoute();
  const deleteMutation = useDeleteGlobalNotificationRoute();
  const toast = useToast();

  const [editor, setEditor] = useState<EditorState>(EDITOR_CLOSED);
  const [remove, setRemove] = useState<RemoveState>(REMOVE_CLOSED);
  const [conflictError, setConflictError] = useState<string | null>(null);

  const routes = routesQuery.data ?? [];

  const handleAdd = (kind: EventKind, channel: Channel | null) => {
    setConflictError(null);
    setEditor({
      open: true,
      mode: 'create',
      eventKind: kind,
      channel,
      routeId: null,
      initialTarget: null,
    });
  };

  const handleEdit = (route: MatrixRoute) => {
    setConflictError(null);
    setEditor({
      open: true,
      mode: 'edit',
      eventKind: route.eventKind,
      channel: route.channel,
      routeId: route.id,
      initialTarget: route.target,
    });
  };

  const handleRemove = (route: MatrixRoute) => {
    const full = routes.find((r) => r.id === route.id);
    if (!full) return;
    setRemove({ open: true, route: full });
  };

  const handleSave = (payload: EditorPayload) => {
    setConflictError(null);
    if (editor.mode === 'create') {
      createMutation.mutate(
        {
          eventKind: payload.eventKind,
          channel: payload.channel,
          target: payload.target,
        },
        {
          onSuccess: () => {
            toast('Route saved', 'success');
            setEditor(EDITOR_CLOSED);
          },
          onError: (err) => {
            if (err instanceof ApiClientError && err.statusCode === 409) {
              setConflictError(
                'A global route for this event kind and channel already exists.',
              );
              return;
            }
            toast('Failed to save route', 'error');
          },
        },
      );
    } else if (editor.routeId != null) {
      updateMutation.mutate(
        {
          routeId: editor.routeId,
          input: { channel: payload.channel, target: payload.target },
        },
        {
          onSuccess: () => {
            toast('Route saved', 'success');
            setEditor(EDITOR_CLOSED);
          },
          onError: (err) => {
            if (err instanceof ApiClientError && err.statusCode === 409) {
              setConflictError(
                'A global route for this event kind and channel already exists.',
              );
              return;
            }
            toast('Failed to save route', 'error');
          },
        },
      );
    }
  };

  const handleConfirmRemove = () => {
    if (!remove.route) return;
    deleteMutation.mutate(remove.route.id, {
      onSuccess: () => {
        toast('Route removed', 'success');
        setRemove(REMOVE_CLOSED);
      },
      onError: () => {
        toast('Failed to remove route', 'error');
        setRemove(REMOVE_CLOSED);
      },
    });
  };

  return (
    <>
      <TopBar
        title="Notification routing — global defaults"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Notification routing' },
        ]}
      />
      <div className="p-6 max-w-5xl mx-auto">
        <p className="text-[12px] text-ms-text-secondary mb-6">
          These defaults apply to every project that has no per-project routes
          for a given event kind. Per-project routes override these defaults
          entirely (no merge).
        </p>
        <Card title="Global default routes">
          {routesQuery.isLoading ? (
            <div className="py-6 flex justify-center">
              <Spinner />
            </div>
          ) : routesQuery.isError ? (
            <div className="text-rose-600 text-[12.5px]">
              Could not load global notification routes.{' '}
              {routesQuery.error instanceof Error
                ? routesQuery.error.message
                : ''}
              . Refresh the page to try again.
            </div>
          ) : (
            <NotificationRoutingMatrix
              routes={routes.map(toMatrixRoute)}
              scope="global"
              onAdd={handleAdd}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />
          )}
        </Card>
      </div>

      <NotificationRouteEditorModal
        open={editor.open}
        mode={editor.mode}
        scope="global"
        initialEventKind={editor.eventKind}
        initialChannel={editor.channel}
        initialTarget={editor.initialTarget}
        conflictError={conflictError}
        isSaving={createMutation.isPending || updateMutation.isPending}
        onSave={handleSave}
        onClose={() => setEditor(EDITOR_CLOSED)}
      />

      <RemoveRouteConfirmModal
        open={remove.open}
        scope="global"
        eventKind={remove.route?.eventKind ?? null}
        channel={remove.route?.channel ?? null}
        isRemoving={deleteMutation.isPending}
        onConfirm={handleConfirmRemove}
        onClose={() => setRemove(REMOVE_CLOSED)}
      />
    </>
  );
}
